import { useState, useEffect, useCallback } from "react";
import {
    Loader2, ArrowDownLeft, ArrowUpRight, CheckCircle2, AlertTriangle,
    RefreshCw, Inbox,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { REFERRAL_SYNC_STATUS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { formatDate } from "../../utils/datetime";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const Stat = ({ label, value, hint, tone }) => (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={cn("text-lg font-semibold",
            tone === "bad" ? "text-red-700"
                : tone === "warn" ? "text-amber-700" : "text-gray-900")}>
            {value}
        </p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
);

/** Whether the two systems are actually talking, in both directions.
 *
 *  The two halves are not symmetrical, and the screen says so rather than
 *  pretending otherwise.
 *
 *  Outbound is precise: every push this system makes records its own outcome,
 *  so an undelivered reward or a stuck terms version names itself and its
 *  error.
 *
 *  Inbound is observed, not reported. eMedHub's pushes are visible here only
 *  as arrivals — a push that failed on its side never reaches us, so it cannot
 *  appear. That gap is covered by eMedHub's own Referral Inbox, which is where
 *  its failed sends are listed.
 */
export default function ReferralSyncStatus() {
    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        fetch(REFERRAL_SYNC_STATUS_URL)
            .then(res => {
                if (res?.status === "success") setData(res.data);
                else ToastMsg("error", res?.message || "Could not load sync status");
            })
            .catch(() => ToastMsg("error", "Could not load sync status"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sync status…
            </div>
        );
    }
    if (!data) return null;

    const { outbound, inbound } = data;
    const callbacksFailing = outbound.stage_callbacks.failing > 0;
    const rewardsStuck = outbound.rewards.undelivered > 0;
    const termsStuck = (outbound.terms || []).some(t => !t.pushed_at);

    return (
        <div className="p-5 space-y-6 max-w-6xl">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Referral Sync</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Whether this system and eMedHub are keeping each other current.
                    </p>
                </div>
                <button
                    type="button" onClick={load} disabled={loading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                        border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                    Refresh
                </button>
            </div>

            {/* ── Inbound ─────────────────────────────────────────────── */}
            <section>
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
                    <ArrowDownLeft className="w-4 h-4 text-gray-500" />
                    From eMedHub
                </h2>
                <p className="text-xs text-gray-500 mb-3">
                    What has arrived. eMedHub's failed sends never reach us, so they
                    are not listed here — its Referral Inbox is where those show.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Stat
                        label="Referrals received"
                        value={inbound.referrals_received}
                        hint={inbound.last_referral_at
                            ? `Latest ${formatDate(inbound.last_referral_at)}`
                            : "None yet"}
                    />
                    <Stat
                        label="Terms acceptances"
                        value={inbound.acceptances_received}
                        hint={inbound.last_acceptance_at
                            ? `Latest ${formatDate(inbound.last_acceptance_at)}`
                            : "None yet"}
                    />
                    <Stat
                        label="Organisations mapped"
                        value={inbound.mapped_organisations}
                        hint="Needed before a reward can be awarded"
                        tone={inbound.mapped_organisations === 0 ? "warn" : undefined}
                    />
                </div>
            </section>

            {/* ── Outbound ────────────────────────────────────────────── */}
            <section className="space-y-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <ArrowUpRight className="w-4 h-4 text-gray-500" />
                    To eMedHub
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Stat
                        label="Stage callbacks failing"
                        value={outbound.stage_callbacks.failing}
                        hint={`of ${outbound.stage_callbacks.total} referral leads`}
                        tone={callbacksFailing ? "bad" : undefined}
                    />
                    <Stat
                        label="Rewards undelivered"
                        value={outbound.rewards.undelivered}
                        hint={`${outbound.rewards.delivered} of ${outbound.rewards.total} delivered`}
                        tone={rewardsStuck ? "bad" : undefined}
                    />
                    <Stat
                        label="Terms versions"
                        value={(outbound.terms || []).length}
                        hint={termsStuck ? "One or more not delivered" : "All delivered"}
                        tone={termsStuck ? "warn" : undefined}
                    />
                </div>

                {/* Terms */}
                {(outbound.terms || []).length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="px-4 py-2.5 border-b border-gray-100">
                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Terms versions
                            </h3>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {outbound.terms.map(t => (
                                <div key={t.version} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                                    <span className="font-medium">v{t.version}</span>
                                    {t.is_active && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full
                                            text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                                            Live
                                        </span>
                                    )}
                                    <span className="flex-1 truncate text-gray-700">{t.title}</span>
                                    {t.pushed_at ? (
                                        <span className="flex items-center gap-1.5 text-xs text-green-700 shrink-0">
                                            <CheckCircle2 className="w-4 h-4" />
                                            {formatDate(t.pushed_at)}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1.5 text-xs text-amber-700 shrink-0"
                                            title={t.push_error || ""}>
                                            <AlertTriangle className="w-4 h-4" />
                                            {t.push_error ? "Failed" : "Pending"}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Undelivered rewards */}
                {rewardsStuck && (
                    <div className="rounded-lg border border-red-200 bg-white">
                        <div className="px-4 py-2.5 border-b border-red-100 bg-red-50/60">
                            <h3 className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                                Rewards the referrer cannot see yet
                            </h3>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {outbound.rewards.rows.map(r => (
                                <div key={r.id} className="px-4 py-2.5 text-sm">
                                    <div className="flex items-center gap-3">
                                        <span className="font-medium text-gray-900">
                                            {r.customer_name || `Customer #${r.id}`}
                                        </span>
                                        <span className="text-gray-700">{money(r.amount)}</span>
                                        <span className="text-xs text-gray-500">
                                            {r.benefit_type} · {r.status}
                                        </span>
                                    </div>
                                    {r.push_error && (
                                        <p className="text-xs text-red-700 mt-1">{r.push_error}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Failing stage callbacks */}
                {callbacksFailing && (
                    <div className="rounded-lg border border-red-200 bg-white">
                        <div className="px-4 py-2.5 border-b border-red-100 bg-red-50/60">
                            <h3 className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                                Stage changes not reaching eMedHub
                            </h3>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {outbound.stage_callbacks.rows.map(l => (
                                <div key={l.referral_code} className="px-4 py-2.5 text-sm">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-mono text-xs">{l.referral_code}</span>
                                        <span className="text-xs text-gray-500">
                                            {l.source_server || "unknown server"}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {l.push_attempts} attempt(s)
                                        </span>
                                        {l.last_pushed_at && (
                                            <span className="text-xs text-gray-500">
                                                last ok {formatDate(l.last_pushed_at)}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-red-700 mt-1">{l.last_error}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!callbacksFailing && !rewardsStuck && !termsStuck && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3
                        text-sm text-green-900 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Everything this system has sent has been delivered.
                    </div>
                )}
            </section>

            <p className="flex items-start gap-2 text-xs text-gray-500 border-t border-gray-100 pt-4">
                <Inbox className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                    Two eMedHub-side pushes — the organisation mapping and the terms
                    acceptance — record no failure state on that server yet, so a
                    failure there is silent in both systems. Their success is still
                    visible above as arrivals.
                </span>
            </p>
        </div>
    );
}
